import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Gauge, HelpCircle, Users, Target, Sparkles,
  BarChart3, CreditCard, Settings as SettingsIcon, Plus, LogOut, ChevronDown, ShieldCheck,
  Menu, X,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [businesses, setBusinesses] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const pathBizMatch = location.pathname.match(/\/app\/isletme\/([^/]+)/);
  const activeId = pathBizMatch?.[1];

  useEffect(() => {
    api.get("/businesses").then((r) => setBusinesses(r.data));
  }, [location.pathname]);

  // Auto-close mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileOpen]);

  const nav = activeId
    ? [
        { to: `/app/isletme/${activeId}`, label: t("nav.overview"), icon: LayoutDashboard, end: true, key: "overview" },
        { to: `/app/isletme/${activeId}/bilgiler`, label: t("nav.myBusiness"), icon: Building2, key: "my-business" },
        { to: `/app/isletme/${activeId}/skor`, label: t("nav.aiScore"), icon: Gauge, key: "ai-score" },
        { to: `/app/isletme/${activeId}/sorular`, label: t("nav.questionTests"), icon: HelpCircle, key: "question-tests" },
        { to: `/app/isletme/${activeId}/rakipler`, label: t("nav.competitors"), icon: Users, key: "competitors" },
        { to: `/app/isletme/${activeId}/gorevler`, label: t("nav.tasks"), icon: Target, key: "tasks" },
        { to: `/app/isletme/${activeId}/icerik`, label: t("nav.contentGenerator"), icon: Sparkles, key: "content-generator" },
        { to: `/app/isletme/${activeId}/rapor`, label: t("nav.reports"), icon: BarChart3, key: "reports" },
      ]
    : [];

  const bottomNav = [
    { to: "/app/planlar", label: t("nav.plans"), icon: CreditCard, key: "plans" },
    { to: "/app/ayarlar", label: t("nav.settings"), icon: SettingsIcon, key: "settings" },
  ];

  const planLabel = user?.plan === "admin"
    ? t("nav.plan.admin")
    : (user?.plan === "free" || !user?.plan)
      ? t("nav.plan.free")
      : t("nav.plan.named", { name: user.plan });

  return (
    <div className="min-h-screen md:flex">
      {/* Mobile topbar — hidden on md+ */}
      <div
        className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#07070D]/90 backdrop-blur-xl"
        data-testid="app-mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg hover:bg-white/5 text-slate-300"
          aria-label={t("nav.menu.open")}
          data-testid="app-menu-open"
        >
          <Menu size={22} />
        </button>
        <Link to={lang === "en" ? "/" : "/tr"} className="inline-flex items-center"><Logo size="sm" /></Link>
        <DropdownMenu>
          <DropdownMenuTrigger
            className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white"
            data-testid="app-mobile-user"
          >
            {user?.email?.charAt(0).toUpperCase()}
          </DropdownMenuTrigger>
          <DropdownMenuContent className="glass-strong border-white/10 text-white" align="end">
            <DropdownMenuItem onClick={() => navigate("/app/ayarlar")} className="cursor-pointer hover:bg-white/5">
              <SettingsIcon size={14} className="mr-2" /> {t("nav.settings")}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              onClick={() => { logout(); navigate(lang === "en" ? "/" : "/tr"); }}
              className="cursor-pointer hover:bg-white/5 text-red-400"
            >
              <LogOut size={14} className="mr-2" /> {t("nav.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="app-drawer-backdrop"
        />
      )}

      {/* Sidebar — drawer on mobile, fixed on desktop */}
      <aside
        className={`fixed md:sticky top-0 left-0 z-50 h-screen w-72 max-w-[85vw] shrink-0 border-r border-white/5 bg-[#07070D]/95 md:bg-[#07070D]/70 backdrop-blur-xl flex flex-col transform transition-transform md:transform-none ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        data-testid="app-sidebar"
      >
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <Link to={lang === "en" ? "/" : "/tr"} data-testid="sidebar-logo-link"><Logo /></Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-2 -mr-2 rounded-lg hover:bg-white/5 text-slate-300"
            aria-label={t("nav.menu.close")}
            data-testid="app-menu-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          <DropdownMenu>
            <DropdownMenuTrigger
              className="w-full glass rounded-xl px-3 py-3 flex items-center justify-between text-left hover:border-white/20 transition-all"
              data-testid="business-switcher"
            >
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("nav.activeBusiness")}</div>
                <div className="text-sm font-medium text-white truncate">
                  {businesses.find((b) => b.id === activeId)?.name || t("nav.selectBusiness")}
                </div>
              </div>
              <ChevronDown size={16} className="text-slate-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass-strong border-white/10 text-white min-w-[260px]" align="start">
              {businesses.map((b) => (
                <DropdownMenuItem
                  key={b.id}
                  onClick={() => navigate(`/app/isletme/${b.id}`)}
                  className="cursor-pointer hover:bg-white/5"
                  data-testid={`switch-business-${b.id}`}
                >
                  <div className="flex flex-col">
                    <span>{b.name}</span>
                    <span className="text-xs text-slate-400">{b.sector} · {b.city}</span>
                  </div>
                </DropdownMenuItem>
              ))}
              {businesses.length > 0 && <DropdownMenuSeparator className="bg-white/10" />}
              <DropdownMenuItem
                onClick={() => navigate("/app/onboarding")}
                className="cursor-pointer hover:bg-white/5 text-cyan-300"
                data-testid="add-business-menu"
              >
                <Plus size={14} className="mr-2" /> {t("nav.addBusiness")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-blue-600/20 to-purple-600/10 text-white border-l-2 border-blue-500"
                    : "text-slate-400 hover:bg-white/[0.04] hover:text-white"
                }`
              }
              data-testid={`nav-${n.key}`}
            >
              <n.icon size={17} strokeWidth={1.6} />
              <span>{n.label}</span>
            </NavLink>
          ))}

          <div className="pt-3 mt-3 border-t border-white/5 space-y-1">
            {bottomNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive ? "bg-white/[0.04] text-white" : "text-slate-400 hover:bg-white/[0.04] hover:text-white"
                  }`
                }
                data-testid={`nav-${n.key}`}
              >
                <n.icon size={17} strokeWidth={1.6} />
                <span>{n.label}</span>
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink
                to="/admin"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 text-cyan-200 hover:from-cyan-500/20 hover:to-purple-500/20"
                data-testid="nav-admin-panel"
              >
                <ShieldCheck size={17} strokeWidth={1.6} />
                <span>{t("nav.adminPanel")}</span>
              </NavLink>
            )}
          </div>

          {/* Language switcher inside the sidebar — visible on every authenticated screen. */}
          <div className="pt-3 mt-3 border-t border-white/5">
            <div className="px-3 pb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              {t("lang.label")}
            </div>
            <div
              className="mx-3 inline-flex items-center gap-1 rounded-lg border border-white/10 px-1 py-0.5"
              data-testid="app-lang-switch"
            >
              <button
                type="button"
                onClick={() => setLang("en")}
                aria-label={t("lang.switchAria", { lang: t("lang.en") })}
                aria-pressed={lang === "en"}
                className={`text-xs font-semibold px-2 py-1 rounded-md transition-colors ${
                  lang === "en" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
                }`}
                data-testid="app-lang-en"
              >
                EN
              </button>
              <span className="text-slate-600 text-xs">|</span>
              <button
                type="button"
                onClick={() => setLang("tr")}
                aria-label={t("lang.switchAria", { lang: t("lang.tr") })}
                aria-pressed={lang === "tr"}
                className={`text-xs font-semibold px-2 py-1 rounded-md transition-colors ${
                  lang === "tr" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
                }`}
                data-testid="app-lang-tr"
              >
                TR
              </button>
            </div>
          </div>
        </nav>

        <div className="p-4 border-t border-white/5 mt-auto">
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full flex items-center gap-3 ghost-btn rounded-xl px-3 py-2.5" data-testid="user-menu">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-xs font-bold">
                {user?.email?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm text-white truncate">{user?.full_name || user?.email}</div>
                <div className="text-[11px] text-slate-500 capitalize" data-testid="user-plan-label">{planLabel}</div>
              </div>
              <ChevronDown size={14} className="text-slate-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass-strong border-white/10 text-white" align="end">
              <DropdownMenuItem
                onClick={() => navigate("/app/ayarlar")}
                className="cursor-pointer hover:bg-white/5"
                data-testid="menu-settings"
              >
                <SettingsIcon size={14} className="mr-2" /> {t("nav.settings")}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={() => { logout(); navigate(lang === "en" ? "/" : "/tr"); }}
                className="cursor-pointer hover:bg-white/5 text-red-400"
                data-testid="logout-btn"
              >
                <LogOut size={14} className="mr-2" /> {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
